import { Injectable } from "@angular/core";
import { AlertController } from "@ionic/angular";
import { AlertButton, AlertOptions } from "@ionic/core";

@Injectable({ providedIn: "root" })
export class LoveItAlertService {
  constructor(private alertController: AlertController) {}

  async create(opts: AlertOptions): Promise<HTMLIonAlertElement> {
    const confirmButton = this.resolveEnterButton(opts.buttons);
    this.highlightEnterButton(confirmButton);
    const alert = await this.alertController.create(opts);
    this.attachEnterKeyHandler(alert, confirmButton);
    return alert;
  }

  private attachEnterKeyHandler(
    alert: HTMLIonAlertElement,
    confirmButton: AlertButton | string | undefined,
  ): void {
    if (!confirmButton) {
      return;
    }

    const role = typeof confirmButton === "string" ? "confirm" : confirmButton.role || "confirm";

    const dismissConfirm = () => alert.dismiss(undefined, role);

    const runHandler = () => {
      if (typeof confirmButton === "string") {
        dismissConfirm();
        return;
      }

      const handler = confirmButton.handler;
      if (!handler) {
        dismissConfirm();
        return;
      }

      const result = handler(undefined as any);

      if (result === false) {
        return;
      }

      if (result && typeof (result as any).then === "function") {
        (result as Promise<any>).then((v) => {
          if (v !== false) {
            dismissConfirm();
          }
        });
        return;
      }

      dismissConfirm();
    };

    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      runHandler();
    };

    const add = () => document.addEventListener("keydown", keydownHandler, true);
    const remove = () => document.removeEventListener("keydown", keydownHandler, true);

    alert.addEventListener("ionAlertDidPresent", add, { once: true });
    alert.addEventListener("ionAlertDidDismiss", remove, { once: true });
  }

  private highlightEnterButton(button: AlertButton | string | undefined): void {
    if (!button || typeof button === "string") {
      return;
    }
    const highlightClass = "enter-confirm-button";
    button.cssClass = button.cssClass ? `${button.cssClass} ${highlightClass}` : highlightClass;
  }

  private resolveEnterButton(buttons?: (AlertButton | string)[]): AlertButton | string | undefined {
    if (!buttons?.length) {
      return;
    }

    if (buttons.length === 1) {
      return buttons[0];
    }

    const nonCancel = buttons.filter((b) => (typeof b === "string" ? true : b.role !== "cancel"));

    if (nonCancel.length) {
      return nonCancel[nonCancel.length - 1];
    }

    return buttons[buttons.length - 1];
  }
}
